import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  setEscolaNaoSelecionadaHandler,
  setUnauthorizedHandler,
  setVersionOutdatedHandler,
} from '@/shared/lib/api/client';
import {
  clearSessionStorage,
  getAccessToken,
  getRefreshToken,
  setTokens,
} from '@/shared/lib/auth/tokenStorage';
import { queryClient, queryKeys } from '@/shared/lib/query/client';
import { VersaoAppBloqueio } from '@/shared/ui';
import type { LoginRequest, MeResponse } from '@/types/api';
import { authService } from './authService';
import { idEscolaParaRestaurar, ensureEscolaAtiva, resolveAuthGate, type AuthGate } from './authGates';
import { desativarPushTokenAtual } from '@/shared/lib/push/pushClient';
import { setSentryUser } from '@/shared/lib/telemetry/sentry';

export type AuthContextValue = {
  user: MeResponse | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authGate: AuthGate;
  login: (payload: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<MeResponse | null>;
  selecionarEscola: (idEscola: number) => Promise<void>;
  /** Atualiza atalhos no cache de /me sem nova requisicao. */
  patchAtalhos: (codigos: number[]) => void;
};

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<MeResponse | null>(null);
  const userRef = useRef<MeResponse | null>(null);
  userRef.current = user;
  const [isLoading, setIsLoading] = useState(true);
  const [versaoBloqueio, setVersaoBloqueio] = useState<string | null>(null);

  const refreshMe = useCallback(async (): Promise<MeResponse | null> => {
    try {
      const me = await authService.me();
      setUser(me);
      queryClient.setQueryData(queryKeys.me, me);
      return me;
    } catch {
      if (!getAccessToken() && !getRefreshToken()) {
        setUser(null);
        queryClient.removeQueries({ queryKey: queryKeys.me });
      }
      return null;
    }
  }, []);

  const boot = useCallback(async () => {
    setIsLoading(true);
    const token = getAccessToken() ?? getRefreshToken();
    if (!token) {
      setUser(null);
      setIsLoading(false);
      return;
    }
    const me = await refreshMe();
    if (me) {
      try {
        await ensureEscolaAtiva(me, async (id) => {
          await authService.selecionarEscola({ idEscola: id });
          await refreshMe();
        });
      } catch {
        /* gate trata escolha */
      }
    }
    setIsLoading(false);
  }, [refreshMe]);

  useEffect(() => {
    void boot();
  }, [boot]);

  useEffect(() => {
    if (user) {
      setSentryUser({ idUsuario: user.idUsuario, codigoCliente: user.codigoCliente });
    } else {
      setSentryUser(null);
    }
  }, [user]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      clearSessionStorage();
      navigate('/login', { replace: true });
    });
    setVersionOutdatedHandler((mensagem) => {
      setVersaoBloqueio(mensagem);
    });
    setEscolaNaoSelecionadaHandler(async () => {
      try {
        const idConhecida = idEscolaParaRestaurar(userRef.current);
        if (idConhecida != null) {
          await authService.selecionarEscola({ idEscola: idConhecida });
          await refreshMe();
          return true;
        }
        const me = await refreshMe();
        const id = idEscolaParaRestaurar(me);
        if (id != null) {
          await authService.selecionarEscola({ idEscola: id });
          await refreshMe();
          return true;
        }
      } catch {
        await refreshMe();
      }
      navigate('/escolher-escola', { replace: true });
      return false;
    });
  }, [navigate, refreshMe]);

  const login = useCallback(
    async (payload: LoginRequest) => {
      const tokens = await authService.login(payload);
      setTokens(tokens.accessToken, tokens.refreshToken);
      const me = await refreshMe();
      if (me) {
        await ensureEscolaAtiva(me, async (id) => {
          await authService.selecionarEscola({ idEscola: id });
          await refreshMe();
        });
      }
    },
    [refreshMe],
  );

  const logout = useCallback(async () => {
    try {
      await desativarPushTokenAtual();
    } catch {
      /* segue */
    }
    try {
      await authService.logout();
    } catch {
      /* idempotente */
    }
    clearSessionStorage();
    setUser(null);
    queryClient.clear();
    navigate('/login', { replace: true });
  }, [navigate]);

  const selecionarEscola = useCallback(
    async (idEscola: number) => {
      await authService.selecionarEscola({ idEscola });
      await refreshMe();
    },
    [refreshMe],
  );

  const patchAtalhos = useCallback((codigos: number[]) => {
    setUser((atual) => {
      if (!atual) return atual;
      const next = { ...atual, atalhos: codigos };
      queryClient.setQueryData(queryKeys.me, next);
      return next;
    });
  }, []);

  const authGate = useMemo(() => resolveAuthGate(user), [user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: Boolean(user && getAccessToken()),
      isLoading,
      authGate,
      login,
      logout,
      refreshMe,
      selecionarEscola,
      patchAtalhos,
    }),
    [user, isLoading, authGate, login, logout, refreshMe, selecionarEscola, patchAtalhos],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      <VersaoAppBloqueio open={versaoBloqueio != null} mensagem={versaoBloqueio ?? undefined} />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return ctx;
}
